// MyAgent Core Module
// Core agent orchestration with LLM execution and tool system

import { ToolCategory, executeCode, createExecuteCodeDefinition } from 'tool_sdk'
import { createNotesCategory } from 'myagent_notes/tools'
import { createNotificationCategory } from 'tool_sdk/tools/notification'
import { createWebCategory } from 'web_tools/tools'
import { forwardCall } from 'workers-common'
import { MYAGENT_BOT_ID, buildBotSystemPrompt, ALL_TOOL_CATEGORIES, MYAGENT_DEFAULT_CONFIG } from './prompts.js'

export { MYAGENT_BOT_ID, buildBotSystemPrompt, ALL_TOOL_CATEGORIES, MYAGENT_DEFAULT_CONFIG }

const ROLE_CALL_TIMEOUT = 30000 // 30s timeout for remote role calls

/**
 * Get tools with category configuration
 * @param {Object} options
 * @param {Function} options.getWorkerClient - Function to get worker RPC client
 * @param {Object} options.categoryConfig - Category configuration
 * @param {string[]} [options.toolCategories] - Enabled tool category IDs (default: all)
 * @returns {Object} { toolDefinitions, executeTool }
 */
export function getTools({ getWorkerClient, categoryConfig, toolCategories }) {
  if (!categoryConfig?.notes?.publicKey && !categoryConfig?.notes?.getTarget) {
    throw new Error('getTools requires categoryConfig.notes.publicKey or notes.getTarget')
  }

  const { notes, searcher } = categoryConfig
  const enabled = toolCategories ? new Set(toolCategories) : null

  // Build categories filtered by toolCategories (null = all enabled)
  const allCategories = [
    { id: 'notes', create: () => createNotesCategory({ getWorkerClient, notesPublicKey: notes.publicKey, getTarget: notes.getTarget }) },
    { id: 'notification', create: () => createNotificationCategory({ getWorkerClient }) },
    { id: 'web', create: () => createWebCategory({ getWorkerClient }) },
  ]
  const categories = allCategories
    .filter(c => !enabled || enabled.has(c.id))
    .map(c => c.create())

  const rootCategory = new ToolCategory({
    id: '',
    description: 'Root category',
    tools: [],
    searcher,
    subCategories: categories,
  })

  const toolDefinitions = [
    createExecuteCodeDefinition(rootCategory),
    {
      name: 'talkToRole',
      description: 'Call a remote role directly. Provide roleId + toolPath + toolArgs. Use searchRoleCapabilities to discover available toolPaths.',
      parameters: {
        type: 'object',
        properties: {
          roleId: { type: 'string', description: 'Role ID (worker public key)' },
          toolPath: { type: 'string', description: 'Tool path (e.g. "tabs/createTab", "code")' },
          toolArgs: { type: 'string', description: 'Tool arguments as JSON string (e.g. \'{"url":"https://google.com"}\')' },
        },
        required: ['roleId', 'toolPath'],
      },
    },
    {
      name: 'searchRoleCapabilities',
      description: "Search a remote role's tools by query. Returns matching tools with their toolPaths.",
      parameters: {
        type: 'object',
        properties: {
          roleId: { type: 'string', description: 'Role ID (worker public key)' },
          query: { type: 'string', description: 'Search query (e.g. "open tab", "screenshot")' },
        },
        required: ['roleId', 'query'],
      },
    },
  ]

  // Cache role capabilities per roleId (60s TTL)
  const roleCapCache = {}
  const ROLE_CAP_TTL = 60000

  const fetchRoleCapabilities = async (ctx, roleId) => {
    const cached = roleCapCache[roleId]
    if (cached && Date.now() - cached.time < ROLE_CAP_TTL) return cached.data

    const workerClient = getWorkerClient()
    const tools = []

    // Recursively collect tools from all category levels
    const walkCategory = async (path, prefix) => {
      const info = await forwardCall(ctx, workerClient, roleId, 'getCategoryInfo', [{ categoryPath: path }], { callTimeout: ROLE_CALL_TIMEOUT })
      for (const t of info.tools || []) {
        tools.push({ path: `${prefix}${t.name}`, name: t.name, description: t.description, category: prefix.slice(0, -1) || path, parameters: t.parameters })
      }
      await Promise.all((info.subCategories || []).map(sub =>
        walkCategory(prefix ? `${prefix}${sub.id}` : sub.id, prefix ? `${prefix}${sub.id}/` : `${sub.id}/`)
      ))
    }

    await walkCategory(undefined, '')
    roleCapCache[roleId] = { data: tools, time: Date.now() }
    return tools
  }

  // Build whitelist of valid tool names from definitions
  const validToolNames = new Set(toolDefinitions.map(t => t.name))

  const executeTool = async (ctx, toolCall) => {
    if (!toolCall?.function) throw new Error('Invalid tool call')

    const toolName = toolCall.function.name

    // Validate tool name against whitelist (prevents LLM prompt injection calling unknown tools)
    if (!validToolNames.has(toolName)) {
      throw new Error('Invalid tool call')
    }

    let args
    try {
      args = typeof toolCall.function.arguments === 'string'
        ? JSON.parse(toolCall.function.arguments)
        : toolCall.function.arguments
    } catch {
      throw new Error('Invalid tool arguments')
    }

    if (toolName === 'executeCode') {
      const result = await executeCode(ctx, args, rootCategory)
      return { toolCallId: toolCall.id, toolName, ...result }
    }

    if (toolName === 'talkToRole') {
      const { roleId, toolPath } = args
      if (!roleId || typeof roleId !== 'string') throw new Error('Invalid roleId')
      if (!toolPath || typeof toolPath !== 'string') throw new Error('Invalid toolPath')

      const workerClient = getWorkerClient()
      const innerToolCalls = []
      const callOpts = { callTimeout: ROLE_CALL_TIMEOUT }
      try {
        const toolArgs = typeof args.toolArgs === 'string' ? JSON.parse(args.toolArgs) : args.toolArgs
        const result = await forwardCall(ctx, workerClient, roleId, 'executeTool', [{ toolPath, toolArgs }], callOpts)
        innerToolCalls.push({ roleId, toolPath, toolArgs, success: true, result, source: 'role' })
        return { toolCallId: toolCall.id, toolName, success: true, result, innerToolCalls }
      } catch (error) {
        innerToolCalls.push({ roleId, toolPath, success: false, error: error.message, source: 'role' })
        return { toolCallId: toolCall.id, toolName, success: false, error: error.message, innerToolCalls }
      }
    }

    if (toolName === 'searchRoleCapabilities') {
      const { roleId, query } = args
      if (!roleId || typeof roleId !== 'string') throw new Error('Invalid roleId')
      if (!query || typeof query !== 'string') throw new Error('Invalid query')

      const tools = await fetchRoleCapabilities(ctx, roleId)
      const strings = tools.map(t => `${t.path}: ${t.description}`)
      const results = await searcher.search(query, strings, { limit: 5 })
      return {
        toolCallId: toolCall.id,
        toolName,
        success: true,
        result: results.map(r => ({ ...tools[r.index], score: r.score })),
      }
    }

    throw new Error('Invalid tool call')
  }

  return { toolDefinitions, executeTool }
}
